import { View, Pressable } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useSharedValue,
} from 'react-native-reanimated';
import { useEffect, useRef, useState } from 'react';
import Colors from '../../constants/Colors';

export default function MapPoint(props) {
  const translateX = useSharedValue(props.point.x);
  const translateY = useSharedValue(props.point.y);

  const [showDeleteButton, setShowDeleteButton] = useState(false);

  useEffect(() => {
    translateX.value = props.point.x;
    translateY.value = props.point.y;
  }, [props.point.x, props.point.y]);

  const handleMoveRectanglePoint = (pointIndex, dx, dy) => {
    const index = pointIndex === 4 ? 0 : pointIndex;
    props.setDrawPoints((prevPoints) => {
      const basePoints = prevPoints.slice(0, 4).map((p) => ({ ...p }));

      const movedPoint = {
        x: basePoints[index].x + dx,
        y: basePoints[index].y + dy,
      };

      const oppIndex = (index + 2) % 4;
      const fixedPoint = basePoints[oppIndex];

      const mid = {
        x: (movedPoint.x + fixedPoint.x) / 2,
        y: (movedPoint.y + fixedPoint.y) / 2,
      };

      const i1 = (index + 1) % 4;
      const i3 = (index + 3) % 4;

      const v1 = {
        x: basePoints[i1].x - ((basePoints[index].x + basePoints[oppIndex].x) / 2),
        y: basePoints[i1].y - ((basePoints[index].y + basePoints[oppIndex].y) / 2),
      };
      const v3 = {
        x: basePoints[i3].x - ((basePoints[index].x + basePoints[oppIndex].x) / 2),
        y: basePoints[i3].y - ((basePoints[index].y + basePoints[oppIndex].y) / 2),
      };

      const newPoints = [];
      newPoints[index] = movedPoint;
      newPoints[oppIndex] = fixedPoint;
      newPoints[i1] = {
        x: mid.x + v1.x,
        y: mid.y + v1.y,
      };
      newPoints[i3] = {
        x: mid.x + v3.x,
        y: mid.y + v3.y,
      };

      return [...newPoints, { ...newPoints[0] }];
    });
  };

  const handleMoveEllipsePoint = (pointIndex, dx, dy) => {
    const index = pointIndex === 4 ? 0 : pointIndex;

    props.setDrawPoints((prevPoints) => {
      const points = prevPoints.slice(0, 4).map((p) => ({ ...p }));

      const moved = {
        x: points[index].x + dx,
        y: points[index].y + dy,
      };

      const oppIndex = (index + 2) % 4;
      const fixed = points[oppIndex];

      const i1 = (index + 1) % 4;
      const i3 = (index + 3) % 4;

      const newPoints = [];

      newPoints[index] = moved;
      newPoints[oppIndex] = fixed;

      newPoints[i1] = { x: moved.x, y: fixed.y };
      newPoints[i3] = { x: fixed.x, y: moved.y };

      return [...newPoints, { ...newPoints[0] }];
    });
  };

  const startPoints = useRef([]);
  const panGestureSen = Gesture.Pan()
    .onBegin(() => {
      startPoints.current = props.drawPoints.map((p) => ({ ...p }));
    })
    .onChange((event) => {
      translateX.value += event.changeX;
      translateY.value += event.changeY;
    })
    .onUpdate((event) => {
      const updatedPoints = startPoints.current.map((p, i) => (i === props.index
        ? { ...p, x: p.x + event.translationX, y: p.y + event.translationY }
        : p));
      props.setPreviewPoints(updatedPoints);
    })
    .onEnd((event) => {
      props.setPreviewPoints(null);
      const dx = event.translationX;
      const dy = event.translationY;
      if (props.selectedTool === 'rectangle') {
        handleMoveRectanglePoint(props.index, dx, dy);
      } else if (props.selectedTool === 'ellipse') {
        handleMoveEllipsePoint(props.index, dx, dy);
      } else {
        let error = false;
        const newArr = Object.assign([], props.drawPoints);
        const newPoint = {
          x: translateX.value,
          y: translateY.value,
          number: props.point.number,
        };
        newArr[props.index] = newPoint;
        if (props.drawPoints.length === props.index + 1
          && props.point.number === props.drawPoints.length - 1) {
          /* if it's the last point,
            then it is on top of the first point so we need to move it to match */
          newArr[0] = {
            x: translateX.value,
            y: translateY.value,
            number: 0,
          };
        }
        if (
          newPoint.x < 0
          || newPoint.y < 0
          || newPoint.x > props.imageWidth
          || newPoint.y > props.imageHeight
        ) {
          error = true;
        }
        if (props.selectedArea) {
          let inside = false;
          for (
            let i = 0, j = props.selectedArea.points.length - 1;
            i < props.selectedArea.points.length;
            // eslint-disable-next-line no-plusplus
            j = i++
          ) {
            const xi = props.selectedArea.points[i].x;
            const yi = props.selectedArea.points[i].y;
            const xj = props.selectedArea.points[j].x;
            const yj = props.selectedArea.points[j].y;

            const intersect = yi > newPoint.y !== yj > newPoint.y
              && newPoint.x < ((xj - xi) * (newPoint.y - yi)) / (yj - yi) + xi;
            if (intersect) inside = !inside;
          }

          if (!inside) {
            error = true;
          }
        }
        if (!error) {
          props.setDrawPoints(newArr);
        } else {
          props.showAlert('Out of bounds!', true);
        }
      }
    });

  return (
    <View
      style={{
        zIndex: 3,
        position: 'absolute',
      }}
      key={props.index}
    >
      <Animated.View style={{
        transform: [{ translateX }, { translateY }],
      }}>
      <GestureDetector gesture={panGestureSen}>
        {/* <Text>{props.point.number}</Text> for debug */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          position: 'absolute',
          top: -10,
          left: -10,
          borderColor: showDeleteButton ? Colors.light.hereworksOrange50 : 'transparent',
          backgroundColor: showDeleteButton ? Colors.light.hereworksOrange50 : 'transparent',
          borderWidth: 3,
          borderRadius: 9,
        }}>
          <Pressable
            onPress={props.tapPoint}
            onHoverIn={() => { if (!props.selectedTool) setShowDeleteButton(true); }}
            onHoverOut={() => setShowDeleteButton(false)}
            style={(state) => [{
              borderRadius: 9,
              flexDirection: 'row',
              alignItems: 'center',
              backgroundColor: state.hovered
                ? '#fff'
                : Colors.light.hereworksOrange50,
              zIndex: 3,
            }]}
            testID={`map-point-${props.index}`}
          >
            <View
              style={{
                width: 18,
                height: 18,
                borderRadius: 9,
                borderWidth: 3,
                borderColor: Colors.light.hereworksOrange500,
              }}
            />
          </Pressable>
          {showDeleteButton ? <Pressable
            onPress={props.deletePoint}
            onHoverIn={() => setShowDeleteButton(true)}
            onHoverOut={() => setShowDeleteButton(false)}
            style={(state) => ({
              zIndex: 3,
              opacity: state.hovered ? 0.5 : 1,
            })}
            testID={`map-delete-point-${props.index}`}
          >
            <View style={{
              marginLeft: 6,
              width: 14,
              height: 14,
              borderRadius: 7,
              borderWidth: 3,
              borderColor: Colors.light.hereworksOrange500,
              backgroundColor: Colors.light.hereworksOrange50,
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              <View style={{
                width: 6, height: 1.5, backgroundColor: Colors.light.hereworksOrange500,
              }} />
            </View>
          </Pressable> : null}
        </View>
      </GestureDetector>
      </Animated.View>
    </View>
  );
}
